import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from './DashboardLayout';
import ComingSoonModal from './ComingSoonModal';
import CallLogs from './CallLogs';
import VoiceSelection from './VoiceSelection';
import BusinessDescription from './BusinessDescription';
import CallRoutingSettings from './CallRoutingSettings';
import SMSConversations from './SMSConversations';
import ServiceManager from './ServiceManager';
import DashboardOverview from './DashboardOverview';

import configService from '../services/configService';

function OperationalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [businessConfig, setBusinessConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [comingSoonModal, setComingSoonModal] = useState({ isOpen: false, featureName: '' });
  const [billingLoading, setBillingLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [expandedSection, setExpandedSection] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [modules, setModules] = useState({});
  const [usageData, setUsageData] = useState(null);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      if (!user) {
        navigate('/login');
        return;
      }
      
      const cognitoUserId = user?.attributes?.sub;
      if (!cognitoUserId) {
        navigate('/setup');
        return;
      }

      setLoading(false);
      
      // Load data in background without blocking
      Promise.allSettled([
        configService.getBusinessConfigByUserId(cognitoUserId),
        configService.getModules(),
        configService.getUsageData()
      ]).then(([config, modulesData, usageDataResult]) => {
        if (config.status === 'fulfilled') {
          const cfg = config.value;
          
          // Check if setup is complete
          const setupComplete = !!(cfg.business_name && cfg.business_description && 
            cfg.phone_number && cfg.assistant_name && cfg.voice_settings && 
            cfg.assistant_phone && cfg.manager_number && cfg.escalation_email);
          
          if (!setupComplete) {
            console.log('🔒 DASHBOARD: Setup incomplete, redirecting to setup');
            navigate('/setup');
            return;
          }
          
          setBusinessConfig(cfg);
        } else if (config.status === 'rejected') {
          // No config found, redirect to setup
          console.log('🔒 DASHBOARD: No config found, redirecting to setup');
          navigate('/setup');
          return;
        }

        if (modulesData.status === 'fulfilled') {
          const moduleMap = {};
          modulesData.value.modules.forEach(m => {
            moduleMap[m.module_id] = m.enabled;
          });
          setModules(moduleMap);
        }

        if (usageDataResult.status === 'fulfilled') {
          setUsageData(usageDataResult.value);
        }
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setLoading(false);
    }
  };

  // Get current sub page info based on active tab
  const getCurrentSubPage = () => {
    const subPages = {
      'overview': { icon: 'fa-tachometer-alt', label: 'Overview' },
      'voice-settings': { icon: 'fa-microphone', label: 'Voice & Personality' },
      'business-description': { icon: 'fa-building', label: 'AI Knowledge' },
      'smart-dispatch': { icon: 'fa-route', label: 'Smart Dispatch' },
      'call-logs': { icon: 'fa-clock-rotate-left', label: 'Call Logs' },
      'sms-conversations': { icon: 'fa-comments', label: 'SMS' },
      'services': { icon: 'fa-briefcase', label: 'Services' }
    };
    return subPages[activeTab] || subPages['overview'];
  };

  return (
    <DashboardLayout>
      <div className="flex flex-1 flex-col">
        <DashboardOverview businessConfig={businessConfig} />
      </div>

      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        onClose={() => setComingSoonModal({ isOpen: false, featureName: '' })}featureName={comingSoonModal.featureName}
      />
    </DashboardLayout>
  );
}

export default OperationalDashboard;